import { SvgIconComponent } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import { Box, ToggleButton, Typography } from "@mui/material";
import React from "react";
import { ONBOARDING_COLORS } from "./onboardingDesign.js";

interface Props extends Omit<
  React.ComponentProps<typeof ToggleButton>,
  "value" | "selected" | "onChange" | "children" | "aria-label"
> {
  label: string;
  description?: string;
  Icon: SvgIconComponent;
  selected: boolean;
  onToggle: () => void;
}

/**
 * A multi-select tile of the wizard: an icon in a rounded square beside the name and
 * a description clamped to two lines (full text as title), a title clamped to two lines, left-aligned, with a check badge once selected. The
 * description is exposed as the accessible description rather than part of the
 * name.
 */
export const OnboardingTile = ({
  label,
  description,
  Icon,
  selected,
  onToggle,
  sx,
  ...buttonProps
}: Props) => {
  const descriptionId = React.useId();

  return (
    <ToggleButton
      {...buttonProps}
      value={label}
      aria-label={label}
      aria-pressed={selected}
      aria-describedby={description ? descriptionId : undefined}
      selected={selected}
      onChange={onToggle}
      sx={[
        {
          position: "relative",
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateColumns: "36px minmax(0, 1fr)",
          columnGap: "12px",
          alignItems: "center",
          alignContent: "center",
          justifyItems: "start",
          textAlign: "left",
          textTransform: "none",
          borderRadius: "12px",
          color: ONBOARDING_COLORS.heading,
          bgcolor: "common.white",
          // Selected: the navy border gets a 1px inset line on top, so it reads
          // 2px wide without moving the content.
          border: `1px solid ${ONBOARDING_COLORS.fieldBorder}`,
          minHeight: 64,
          p: "14px 40px 14px 14px",
          letterSpacing: "normal",
          "&:hover": {
            borderColor: ONBOARDING_COLORS.navy,
            bgcolor: "common.white",
          },
          "&.Mui-selected": {
            borderColor: ONBOARDING_COLORS.navy,
            boxShadow: `inset 0 0 0 1px ${ONBOARDING_COLORS.navy}`,
            bgcolor: ONBOARDING_COLORS.dsTint,
            color: ONBOARDING_COLORS.heading,
            "&:hover": { bgcolor: ONBOARDING_COLORS.dsTint },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {selected && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            right: 12,
            width: 22,
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: ONBOARDING_COLORS.navy,
            color: "common.white",
          }}
        >
          <CheckIcon sx={{ fontSize: 15 }} />
        </Box>
      )}
      <Box
        aria-hidden
        sx={{
          width: 36,
          height: 36,
          gridRow: description ? "1 / span 2" : undefined,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          color: ONBOARDING_COLORS.dsNavy,
          bgcolor: selected ? "common.white" : ONBOARDING_COLORS.dsTint,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Typography
        component="span"
        title={label}
        sx={{
          maxWidth: "100%",
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.3,
          letterSpacing: "normal",
          // At most two lines; longer names are cut with an ellipsis.
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          color: ONBOARDING_COLORS.heading,
        }}
      >
        {label}
      </Typography>
      {description && (
        <Typography
          id={descriptionId}
          component="span"
          title={description}
          sx={{
            maxWidth: "100%",
            mt: "1px",
            fontSize: 12,
            lineHeight: 1.4,
            letterSpacing: "normal",
            fontWeight: 400,
            // Up to two lines, so a longer subline ("Installation, Smart
            // Home, E-Mobility") is not cut on a narrower tile.
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            color: ONBOARDING_COLORS.muted,
          }}
        >
          {description}
        </Typography>
      )}
    </ToggleButton>
  );
};
